import React from 'react'
import { MaybeLoaded } from 'json-react-layouts-data-loader'

import { Skeleton } from 'component-library/content/Skeleton/Skeleton'
import { toLinkWithHint } from 'common/routing/route-hints'

import { CommonCardProps } from 'component-library/cards/CardItem.Props'

import {
    StyledListCard,
    StyledLink,
    StyledHeadline,
    StyledTopicLabel,
    StyledLinkText,
    StyledKicker,
    StyledTimestamp,
    StyledIconPremium,
} from 'component-library/cards/ListCard/ListCard.styled'
import { ThemeProps } from 'component-library/__styling/themes'
import { withTheme } from '@emotion/react'
import { PublicationCardItem } from 'common/client-data-types/card-types'
import { createCardClickedEvent } from 'common/events/collection-events'
import { TimestampType } from 'component-library/content/CardTimestamp/CardTimestamp'

export interface Props extends CommonCardProps {
    timestamp?: TimestampType
    simpleList?: boolean
    noStretch?: boolean
    removeHorizontalMargin?: boolean
    hideTopic?: boolean
    isTruncated?: boolean
    hasSeparators?: boolean
    cardNumber: number

    item: MaybeLoaded<PublicationCardItem>
}

class InternalListCard extends React.Component<Props & ThemeProps, {}> {
    linkClicked = (e: React.MouseEvent<HTMLElement>) => {
        if (!this.props.item.loaded) {
            // We don't want the link to do anything
            e.preventDefault()
            return
        }

        this.props.onEvent(
            createCardClickedEvent(
                this.props.item.result,
                'InternalListCard',
                'List',
                this.props.cardNumber
            )
        )
    }

    renderTopicOrTimestamp = (showTimestamp: boolean) => {
        const { item, timestamp, hideTopic, theme } = this.props

        if (!item.loaded) {
            if (timestamp !== undefined) {
                return <StyledTimestamp loading={true} />
            }
            return
        }

        if (!item) {
            return
        }

        if (showTimestamp) {
            const time = item.result.publicationDate
            return <StyledTimestamp time={time} />
        }

        if (!hideTopic) {
            return theme.kind === 'perthnow' ? (
                <StyledTopicLabel
                    sizing="small"
                    topic={item.result.primaryTopic}
                    text={item.result.primaryTopic.title}
                />
            ) : (
                <StyledKicker showVideoIcon={false} isLive={false}>
                    {item.result.primaryTopic.title}
                </StyledKicker>
            )
        }

        return
    }

    renderListCardContent() {
        const { timestamp, simpleList, theme } = this.props

        //prettier-ignore
        const showTimestamp =
            timestamp !== undefined && this.props.item.loaded && !!this.props.item.result.publicationDate

        const pnTimestamp =
            theme.kind === 'perthnow' ? this.renderTopicOrTimestamp(showTimestamp) : null
        const sevenTimestamp =
            theme.kind === 'sevennews' ? this.renderTopicOrTimestamp(showTimestamp) : null
        const isPremium = !this.props.item.loaded ? false : this.props.item.result.isPremium

        return (
            <React.Fragment>
                {pnTimestamp}
                <StyledLink
                    to={toLinkWithHint(this.props.item)}
                    onClick={this.linkClicked}
                    timestamp={timestamp}
                    removeHorizontalMargin={
                        this.props.removeHorizontalMargin ||
                        (!this.props.item.loaded && !this.props.timestamp)
                    }
                    simpleList={simpleList}
                    hasSeparators={this.props.hasSeparators}
                >
                    {sevenTimestamp}
                    <StyledLinkText isTruncated={this.props.isTruncated}>
                        {isPremium && <StyledIconPremium width={17} height={17} />}
                        {!this.props.item.loaded ? (
                            <Skeleton />
                        ) : (
                            this.props.item.result.shortHeadline
                        )}
                    </StyledLinkText>
                </StyledLink>
            </React.Fragment>
        )
    }

    render() {
        const { className, simpleList, noStretch } = this.props

        return (
            <StyledListCard
                className={className}
                data-topic={
                    !this.props.item.loaded ? undefined : this.props.item.result.primaryTopic.id
                }
                simpleList={simpleList}
                noStretch={noStretch}
            >
                {this.props.theme.kind === 'perthnow' ? (
                    <StyledHeadline>{this.renderListCardContent()}</StyledHeadline>
                ) : (
                    this.renderListCardContent()
                )}
            </StyledListCard>
        )
    }
}

export const ListCard = withTheme(InternalListCard)
